import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

/**
 * Palmyra X5 (Writer) via Lambda.
 *
 * Compatible with Lambda responses that may return:
 * - direct JSON: { text, model_output, ... }
 * - Lambda proxy JSON body string: { body: "{\"text\":\"...\"}" }
 * - Converse-style nested output payloads
 * - legacy choices/message/content payloads
 */
@Injectable({ providedIn: 'root' })
export class PalmyraX5IqService {
  private readonly endpoint =
    'https://a4yynqrtytduceoe5pruy3rk3q0zwgiq.lambda-url.us-east-1.on.aws/';

  constructor(private http: HttpClient) {}

  analyzePromptAndWritingSample(prompt: string, writingSample: string): Observable<string> {
    const p = String(prompt ?? '').trim();
    const w = String(writingSample ?? '').trim();

    if (!p && !w) {
      return throwError(() => new Error('Prompt or writing sample is required for X5 analysis.'));
    }

    const body = this.buildRequestBody(p, w);

    return this.http
      .post<unknown>(this.endpoint, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      })
      .pipe(
        map((resp) => {
          const text = this.extractText(resp);
          if (!text) {
            throw new Error('No output from Palmyra X5.');
          }
          return text;
        }),
        catchError((err: any) => {
          const msg =
            this.extractErrorMessage(err) ||
            'Palmyra X5 request failed.';

          return throwError(() => new Error(String(msg)));
        })
      );
  }

  private buildRequestBody(prompt: string, writingSample: string): Record<string, unknown> {
    const parts: string[] = [];

    if (prompt) {
      parts.push(prompt);
    }

    if (writingSample) {
      parts.push(`--- Writing sample for IQ / style analysis ---\n${writingSample}`);
    }

    const effectiveUserInput = parts.join('\n\n').trim() || writingSample || prompt;

    return {
      prompt: effectiveUserInput,
      userprompt: effectiveUserInput,
      message: effectiveUserInput,
      writingSample,
      originalPrompt: prompt
    };
  }

  private extractErrorMessage(err: any): string {
    const candidates: unknown[] = [
      err?.error?.error,
      err?.error?.message,
      err?.message,
      err?.error,
      err
    ];

    for (const candidate of candidates) {
      const msg = this.readPossibleError(candidate);
      if (msg) {
        return msg;
      }
    }

    return '';
  }

  private readPossibleError(value: unknown): string {
    if (value == null) {
      return '';
    }

    if (typeof value === 'string') {
      const parsed = this.tryParseJsonObject(value);
      if (parsed) {
        return this.readPossibleError(parsed);
      }
      return value.trim();
    }

    if (typeof value !== 'object') {
      return String(value).trim();
    }

    const obj = value as Record<string, unknown>;

    const directFields = [
      obj.error,
      obj.message,
      obj.detail,
      obj.title
    ];

    for (const field of directFields) {
      if (typeof field === 'string' && field.trim()) {
        return field.trim();
      }
    }

    if (typeof obj.body === 'string' && obj.body.trim()) {
      const nested = this.readPossibleError(obj.body);
      if (nested) {
        return nested;
      }
    }

    if (obj.raw) {
      const nestedRaw = this.readPossibleError(obj.raw);
      if (nestedRaw) {
        return nestedRaw;
      }
    }

    return '';
  }

  private tryParseBodyError(body: string): string {
    try {
      const o = JSON.parse(body) as { error?: string; message?: string; raw?: unknown };
      if (o?.error && String(o.error).trim()) return String(o.error).trim();
      if (o?.message && String(o.message).trim()) return String(o.message).trim();
      if (o?.raw) return this.readPossibleError(o.raw);
      return '';
    } catch {
      return '';
    }
  }

  private tryParseJsonObject(value: string): Record<string, unknown> | null {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object'
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  private extractText(resp: unknown): string {
    if (resp == null) return '';

    if (typeof resp === 'string') {
      const parsed = this.tryParseJsonObject(resp);
      return parsed ? this.textFromPayload(parsed) : resp.trim();
    }

    if (typeof resp !== 'object') {
      return String(resp).trim();
    }

    const r = resp as Record<string, unknown>;

    if (typeof r.body === 'string') {
      try {
        const inner = JSON.parse(r.body) as Record<string, unknown>;
        const innerText = this.textFromPayload(inner);
        if (innerText) return innerText;
      } catch {
        const rawBody = String(r.body).trim();
        if (rawBody) return rawBody;
      }
    }

    if (r.body && typeof r.body === 'object') {
      const innerText = this.textFromPayload(r.body as Record<string, unknown>);
      if (innerText) return innerText;
    }

    return this.textFromPayload(r);
  }

  private textFromPayload(o: Record<string, unknown>): string {
    const direct = o.text ?? o.model_output;
    if (typeof direct === 'string' && direct.trim()) {
      return direct.trim();
    }

    const output = this.asRecord(o.output);
    const outputMessage = this.asRecord(output?.message);
    const outputContentText = this.textFromConverseContent(outputMessage?.content);
    if (outputContentText) {
      return outputContentText;
    }

    const message = this.asRecord(o.message);
    const messageContentText = this.textFromConverseContent(message?.content);
    if (messageContentText) {
      return messageContentText;
    }

    const contentText = this.textFromConverseContent(o.content);
    if (contentText) {
      return contentText;
    }

    const choices = o.choices;
    if (Array.isArray(choices) && choices.length > 0) {
      const first = this.asRecord(choices[0]);
      const firstMessage = this.asRecord(first?.message);

      const firstContent = firstMessage?.content;
      if (typeof firstContent === 'string' && firstContent.trim()) {
        return firstContent.trim();
      }

      const legacyBlockText = this.textFromConverseContent(firstContent);
      if (legacyBlockText) {
        return legacyBlockText;
      }
    }

    const raw = this.asRecord(o.raw);
    if (raw) {
      const rawText = this.textFromPayload(raw);
      if (rawText) {
        return rawText;
      }
    }

    return '';
  }

  private textFromConverseContent(content: unknown): string {
    if (typeof content === 'string' && content.trim()) {
      return content.trim();
    }

    if (!Array.isArray(content)) {
      return '';
    }

    const parts: string[] = [];

    for (const item of content) {
      const block = this.asRecord(item);
      if (!block) continue;

      const text = block.text;
      if (typeof text === 'string' && text.trim()) {
        parts.push(text.trim());
        continue;
      }

      const nested = this.asRecord(block.content);
      if (nested && typeof nested.text === 'string' && nested.text.trim()) {
        parts.push(nested.text.trim());
      }
    }

    return parts.join('\n').trim();
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }
}